import { useLiveQuery } from "dexie-react-hooks";
import { db, type Habit, type Review, type ReviewKind } from "@/lib/db";
import { habitHeatmap, type HeatDay } from "@/lib/heatmap";
import { computeStreak, type Streak } from "@/lib/streak";
import { loadWeeklySummaries } from "@/lib/week-data";
import type { WeeklySummary } from "@/lib/weekly-summary";

export type HabitStatus = { habit: Habit; streak: Streak };

/**
 * Every live habit with its streak as of `today`, active ones first. Logs are
 * small (one row per habit per day), so streaks are computed from all of them.
 */
export function useHabitStatuses(today: string): HabitStatus[] | undefined {
	return useLiveQuery(async () => {
		const [habits, logs, mornings] = await Promise.all([
			db.habits.filter((h) => !h.deleted_at).toArray(),
			db.habit_logs.filter((l) => !l.deleted_at).toArray(),
			db.reviews.filter((r) => r.kind === "morning" && r.minimum_day && !r.deleted_at).toArray(),
		]);
		const minimumDays = new Set(mornings.map((r) => r.date));
		const byHabit = new Map<string, { date: string; count: number }[]>();
		for (const l of logs) byHabit.set(l.habit_id, [...(byHabit.get(l.habit_id) ?? []), l]);
		return habits
			.sort(
				(a, b) =>
					Number(a.archived) - Number(b.archived) || a.created_at.localeCompare(b.created_at),
			)
			.map((habit) => ({
				habit,
				streak: computeStreak(habit, byHabit.get(habit.id) ?? [], today, minimumDays),
			}));
	}, [today]);
}

/** The review for `kind` on `day` (weekly: the Monday), or null. */
export function useReview(kind: ReviewKind, day: string): Review | null | undefined {
	return useLiveQuery(async () => {
		const r = await db.reviews.where("[kind+date]").equals([kind, day]).first();
		return r && !r.deleted_at ? r : null;
	}, [kind, day]);
}

/** Today's morning review (holds the minimum-day flag, D50). */
export function useMorningReview(day: string): Review | null | undefined {
	return useReview("morning", day);
}

/** The week's auto summary from local rows (RV-3, D51). */
export function useWeeklySummary(week: string, today: string): WeeklySummary | undefined {
	return useLiveQuery(async () => (await loadWeeklySummaries([week], today))[0], [week, today]);
}

/** The last `weeks` weeks of daily habit results (HB-4). */
export function useHabitHeatmap(today: string, weeks = 12): HeatDay[][] | undefined {
	return useLiveQuery(async () => {
		const [habits, logs, reviews] = await Promise.all([
			db.habits.toArray(),
			db.habit_logs.toArray(),
			db.reviews.toArray(),
		]);
		const minimumDays = new Set(
			reviews
				.filter((r) => r.kind === "morning" && r.minimum_day && !r.deleted_at)
				.map((r) => r.date),
		);
		return habitHeatmap({ habits, logs, minimumDays, today, weeks });
	}, [today, weeks]);
}
